import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { authAPI, patientAPI, reportAPI, imagingAPI, excelAPI, normalizePatient, normalizeReport } from "../utils/api";

const AppContext = createContext();

const TOKEN_KEY = "emr_token";
const USER_KEY  = "emr_user";

export function AppProvider({ children }) {
  const [user, setUser]           = useState(() => {
    try { return JSON.parse(localStorage.getItem(USER_KEY)) || null; } catch { return null; }
  });
  const [patients, setPatients]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [backendOk, setBackendOk]       = useState(null);
  const [pagination, setPagination]     = useState({ page: 1, total: 0, total_pages: 1 });
  const [searchQuery, setSearchQuery]   = useState("");
  const [sessionExpired, setSessionExpired] = useState(false);

  const logoutTimer = useRef(null);

  // ── Backend health check ──────────────────────────────────────────────────
  useEffect(() => {
    fetch((window.location.hostname === "localhost" ? "http://localhost:8000" : "https://nashman-prod-production.up.railway.app") + "/health")
      .then(r => r.ok ? setBackendOk(true) : setBackendOk(false))
      .catch(() => setBackendOk(false));
  }, []);

  // ── Auto logout when JWT expires ─────────────────────────────────────────
  const scheduleAutoLogout = useCallback((expiresInSeconds) => {
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    // Logout 30 seconds before expiry to be safe
    const ms = Math.max(0, (expiresInSeconds - 30) * 1000);
    logoutTimer.current = setTimeout(() => {
      setSessionExpired(true);
      doLogout();
    }, ms);
  }, []);

  useEffect(() => {
    // On mount, check if existing token is still valid
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && user) {
      authAPI.verifyToken(token)
        .then(res => {
          if (!res.valid) doLogout();
        })
        .catch(() => doLogout());
    }
    return () => { if (logoutTimer.current) clearTimeout(logoutTimer.current); };
  }, []);

  // ── Load patients on login ────────────────────────────────────────────────
  const loadPatients = useCallback(async (page = 1, search = "") => {
    try {
      setLoading(true);
      const data = await patientAPI.list(search, page, 20);
      const pts = data.patients || [];

      // If page returns empty and we're not on page 1, auto-redirect to page 1
      if (pts.length === 0 && page > 1 && data.total > 0) {
        const p1 = await patientAPI.list(search, 1, 20);
        setPatients((p1.patients || []).map(normalizePatient));
        setPagination({ page: 1, total: p1.total, total_pages: p1.total_pages });
        setSearchQuery(search);
        return;
      }

      setPatients(pts.map(normalizePatient));
      setPagination({ page: data.page, total: data.total, total_pages: data.total_pages });
      setSearchQuery(search);
    } catch (e) {
      if (e.message?.includes("expired") || e.message?.includes("401")) {
        setSessionExpired(true);
        doLogout();
      }
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAllPatients = useCallback(async (search = "") => {
    const data = await patientAPI.listAll(search);
    return data.map(normalizePatient);
  }, []);

  useEffect(() => {
    if (user && backendOk) loadPatients(1, "");
  }, [user, backendOk, loadPatients]);

  // ── AUTH ──────────────────────────────────────────────────────────────────
  const login = async (username, password) => {
    try {
      const res = await authAPI.login(username, password);
      const u = { role: res.role, username: res.username };

      setUser(u);
      setSessionExpired(false);
      localStorage.setItem(USER_KEY, JSON.stringify(u));
      // Store JWT token
      if (res.token) {
        localStorage.setItem(TOKEN_KEY, res.token);
        if (res.expires_in) scheduleAutoLogout(res.expires_in);
      }
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const doLogout = () => {
    setUser(null);
    setPatients([]);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_KEY);
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
  };

  const logout = () => {
    setSessionExpired(false);
    doLogout();
  };

  const changePassword = async (role, newPassword, editorRole) => {
    return await authAPI.changePassword(role, newPassword, editorRole || user?.role);
  };

  const verifyMasterCode = async (code, newPassword) => {
    try {
      await authAPI.emergencyReset(code, newPassword, "admin");
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const updateMasterCode = async (current, newCode) => {
    try {
      await authAPI.updateMasterCode(current, newCode);
      return { success: true };
    } catch (e) {
      return { success: false, error: e.message };
    }
  };

  const getAuditLog = async (limit = 100) => {
    return await authAPI.getAuditLog(limit);
  };

  // ── PATIENTS ──────────────────────────────────────────────────────────────
  const addPatient = async (data) => {
    const created = await patientAPI.create(data);
    await loadPatients(1, "");
    return normalizePatient(created).id;
  };

  const deletePatient = async (id) => {
    // Optimistic — remove from UI immediately
    setPatients(prev => prev.filter(p => p.id !== id));
    setPagination(prev => ({ ...prev, total: Math.max(0, (prev.total || 1) - 1) }));
    // Fire delete in background — no need to await reload
    try {
      await patientAPI.delete(id);
    } catch (err) {
      // On failure, reload to restore correct state
      await loadPatients(pagination.page, searchQuery);
      throw err;
    }
  };

  const updatePatient = async (id, data) => {
    const updated = await patientAPI.update(id, data);
    const normalized = normalizePatient(updated);
    setPatients(prev => prev.map(p => p.id === id ? normalized : p));
  };

  const refreshPatient = async (id) => {
    const fresh = await patientAPI.get(id);
    const normalized = normalizePatient(fresh);
    setPatients(prev => prev.map(p => p.id === id ? normalized : p));
    return normalized;
  };

  const searchPatients = async (query) => {
    const data = await patientAPI.list(query);
    return data.map(normalizePatient);
  };

  // ── REPORTS ───────────────────────────────────────────────────────────────
  const addReport = async (patientId, { file, type, lab, labId, date, time, patientSex }) => {
    const formData = new FormData();
    formData.append("patient_id",  patientId);
    formData.append("report_type", type);
    formData.append("lab",         lab || "");
    formData.append("lab_id",      labId || "");
    formData.append("date",        date || "");
    formData.append("time",        time || "");
    formData.append("patient_sex", patientSex || "Female");
    formData.append("file",        file);

    const result = await reportAPI.upload(formData);
    const normalized = normalizeReport(result);

    setPatients(prev => prev.map(p => {
      if (p.id !== patientId) return p;
      return { ...p, reports: [...p.reports, normalized] };
    }));

    return {
      report:           normalized,
      extractionError:  result.extraction_error,
      extractionNote:   result.extraction_note,
      extractionMethod: result.extraction_method,
    };
  };

  const editReport = async (patientId, reportId, updatedValues, editorRole) => {
    const backendValues = updatedValues.map(v => ({
      name:       v.name,
      value:      typeof v.value === "number" ? v.value : (isNaN(parseFloat(v.value)) ? null : parseFloat(v.value)),
      value_text: String(v.value ?? v.valueText ?? ""),
      unit:       v.unit,
      ref_min:    v.refMin,
      ref_max:    v.refMax,
      status:     v.status || "Needs Review",
      abnormal:   v.abnormal,
    }));

    const updated = await reportAPI.edit(reportId, backendValues, editorRole);
    const normalized = normalizeReport(updated);

    setPatients(prev => prev.map(p => {
      if (p.id !== patientId) return p;
      return { ...p, reports: p.reports.map(r => r.id === reportId ? normalized : r) };
    }));

    return normalized;
  };

  const deleteReport = async (patientId, reportId) => {
    await reportAPI.delete(reportId);
    setPatients(prev => prev.map(p => {
      if (p.id !== patientId) return p;
      return { ...p, reports: p.reports.filter(r => r.id !== reportId) };
    }));
  };

  // ── IMAGING ───────────────────────────────────────────────────────────────
  const addImaging = async (patientId, data, file) => {
    const formData = new FormData();
    formData.append("patient_id",  patientId);
    formData.append("modality",    data.modality || "X-Ray");
    formData.append("date",        data.date || "");
    formData.append("time",        data.time || "");
    formData.append("description", data.description || "");
    formData.append("findings",    data.findings || "");
    formData.append("radiologist", data.radiologist || "");
    formData.append("created_by",  user?.role || "");
    formData.append("file",        file);
    return await imagingAPI.upload(formData);
  };

  const updateImaging = async (id, data) => imagingAPI.update(id, data);
  const deleteImaging = async (id) => imagingAPI.delete(id);
  const getPatientImaging = async (patientId) => imagingAPI.getForPatient(patientId);
  const getImagingFileUrl = (id) => imagingAPI.fileUrl(id);

  // ── EXCEL ─────────────────────────────────────────────────────────────────
  const exportToExcel = (patient) => {
    window.open(excelAPI.exportUrl(patient.id), "_blank");
  };

  const importFromExcel = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const result = await excelAPI.import(formData);
    // Don't reload here — caller handles reload after all imports done
    return result;
  };

  const importBatch = async (files) => {
    const formData = new FormData();
    files.forEach(f => formData.append("files", f));
    const result = await excelAPI.importBatch(formData);
    await loadPatients(1, "");
    return result;
  };

  const getPdfUrl = (reportId) => {
    const patient = patients.find(p => p.reports?.some(r => r.id === reportId));
    const report  = patient?.reports?.find(r => r.id === reportId);
    if (report?.storageUrl) return report.storageUrl;
    return reportAPI.pdfUrl(reportId);
  };

  return (
    <AppContext.Provider value={{
      user, login, logout,
      patients, loading, error, backendOk, sessionExpired,
      pagination, searchQuery,
      loadPatients, loadAllPatients, searchPatients, refreshPatient,
      addPatient, deletePatient, updatePatient,
      addReport, editReport, deleteReport,
      addImaging, updateImaging, deleteImaging, getPatientImaging, getImagingFileUrl,
      changePassword, verifyMasterCode, updateMasterCode, getAuditLog,
      exportToExcel, importFromExcel, importBatch,
      getPdfUrl,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
